/**
 * @file useSitePreviewState.ts
 * @description Состояние предпросмотра сайта — одно на весь каркас вида
 * сайта (phase19.16 Р1; подготовка к оболочке «Сайт → Конструктор», фаза
 * §7.14: один iframe на все панели): экран, товар или страница, ширина,
 * «Черновик | Сейчас на сайте», выбранная область (шапка или подвал).
 *
 * Панель сообщает, что ей показать, через `focus({ screen, area })`:
 * «Лента блога» — экран «Блог», «Шапка и подвал» — шапку или подвал (канва
 * прокручивается сообщением `scroll-to`, без перезагрузки). Клик по шапке
 * или подвалу на канве — `pickArea` (счётчик `areaPicks`). Конструктор
 * берёт отсюда ширину и «Черновик | Сейчас», экран и выбор области — из
 * своего адреса. Выбор человека (ширина) при переходе между панелями
 * сохраняется.
 */

import { useCallback, useMemo, useRef, useState } from "react";

import { usePreviewTargets, type PreviewTargets } from "./usePreviewTargets";
import type { CanvasArea } from "../../lib/site-view/canvas-messages";
import type { PreviewMode, PreviewScreenKey, PreviewWidth } from "../../lib/site-view/preview";

export interface SitePreviewFocus {
  screen?: PreviewScreenKey;
  /** Область сайта: шапка или подвал; `null` — без выбора. */
  area?: CanvasArea | null;
}

export interface SitePreviewState {
  screen: PreviewScreenKey;
  /** Выбранный товар или страница (slug); `null` — первый в списке. */
  target: string | null;
  width: PreviewWidth;
  mode: PreviewMode;
  area: CanvasArea | null;
  /** Сколько раз область выбрали кликом на канве (сигнал «открыть шапку и подвал»). */
  areaPicks: number;
  targets: PreviewTargets;
  setScreen: (screen: PreviewScreenKey) => void;
  setTarget: (slug: string | null) => void;
  setWidth: (width: PreviewWidth) => void;
  setMode: (mode: PreviewMode) => void;
  /** Что показать для открытой панели (стабильная функция). */
  focus: (next: SitePreviewFocus) => void;
  /** Клик по области на канве (стабильная функция). */
  pickArea: (area: CanvasArea) => void;
}

/** Начальная ширина: на телефоне — 360, иначе — компьютер. */
function initialWidth(): PreviewWidth {
  return typeof window !== "undefined" && window.innerWidth < 768 ? 360 : 1280;
}

export function useSitePreviewState(): SitePreviewState {
  const targets = usePreviewTargets();
  const [screen, setScreenState] = useState<PreviewScreenKey>("home");
  const [target, setTarget] = useState<string | null>(null);
  const [width, setWidth] = useState<PreviewWidth>(initialWidth);
  const [mode, setMode] = useState<PreviewMode>("draft");
  const [area, setArea] = useState<CanvasArea | null>(null);
  const [areaPicks, setAreaPicks] = useState(0);
  // Свежий ensure без перезапуска стабильных функций.
  const ensureRef = useRef(targets.ensure);
  ensureRef.current = targets.ensure;

  const setScreen = useCallback((next: PreviewScreenKey) => {
    setScreenState(next);
    setTarget(null);
    if (next === "product" || next === "page") ensureRef.current(next);
  }, []);

  const focus = useCallback(
    (next: SitePreviewFocus) => {
      if (next.screen) setScreen(next.screen);
      if (next.area !== undefined) setArea(next.area);
    },
    [setScreen],
  );

  const pickArea = useCallback((next: CanvasArea) => {
    setArea(next);
    setAreaPicks((n) => n + 1);
  }, []);

  return useMemo(
    () => ({ screen, target, width, mode, area, areaPicks, targets, setScreen, setTarget, setWidth, setMode, focus, pickArea }),
    [screen, target, width, mode, area, areaPicks, targets, setScreen, focus, pickArea],
  );
}
